import { useState, useEffect } from 'react'
import { fetchGames, fetchGameScores } from '@/lib/games'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Trophy, Medal, Star } from "lucide-react"
import type { Game, GameScore } from '@/types/games'

export default function LeaderboardPage() {
  const [games, setGames] = useState<Game[]>([])
  const [selectedGame, setSelectedGame] = useState<Game | null>(null)
  const [scores, setScores] = useState<GameScore[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMode, setSelectedMode] = useState<string>('all')
  const [selectedType, setSelectedType] = useState<string>('all')

  useEffect(() => {
    loadGames()
  }, [])

  useEffect(() => {
    if (selectedGame) {
      loadScores(selectedGame.id)
    }
  }, [selectedGame])

  async function loadGames() {
    try {
      const availableGames = await fetchGames()
      setGames(availableGames)
      if (availableGames.length > 0) {
        setSelectedGame(availableGames[0])
      }
    } catch (error) {
      console.error('Failed to oad games:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadScores(gameId: string) {
    try {
      const gameScores = await fetchGameScores(gameId)
      setScores(gameScores)
    } catch (error) {
      console.error('Failed to load scores!:', error)
    }
  }

  const filteredGames = games.filter(game => {
    const matchesSearch = game.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false
    const matchesMode = selectedMode === 'all' || game.mode === selectedMode
    const matchesType = selectedType === 'all' || game.type === selectedType
    return matchesSearch && matchesMode && matchesType
  })

  const getMedalIcon = (position: number) => {
    switch (position) {
      case 0:
        return <Trophy className="h-6 w-6 text-yellow-400" />
      case 1:
        return <Medal className="h-6 w-6 text-gray-300" />
      case 2:
        return <Medal className="h-6 w-6 text-[#CD7F32]" />
      default:
        return <Star className="h-6 w-6 text-muted-foreground/40" />
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <p className="text-muted-foreground">Loading leaderboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 className="text-3xl font-semibold">Leaderboard</h1>

        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            placeholder="Search games..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="sm:max-w-xs"
          />
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedMode === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedMode('all')}
            >
              All Modes
            </Button>
            <Button
              variant={selectedMode === 'trivia' ? 'default' : 'outline'}
              onClick={() => setSelectedMode('trivia')}
            >
              Trivia
            </Button>
            <Button
              variant={selectedMode === 'memory' ? 'default' : 'outline'}
              onClick={() => setSelectedMode('memory')}
            >
              Memory
            </Button>
            <Button
              variant={selectedMode === 'sequence' ? 'default' : 'outline'}
              onClick={() => setSelectedMode('sequence')}
            >
              Sequence
            </Button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedType === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedType('all')}
          >
            All Types
          </Button>
          <Button
            variant={selectedType === 'best_of_three' ? 'default' : 'outline'}
            onClick={() => setSelectedType('best_of_three')}
          >
            Best of Three
          </Button>
          <Button
            variant={selectedType === 'total_score' ? 'default' : 'outline'}
            onClick={() => setSelectedType('total_score')}
          >
            Total Score
          </Button>
        </div>

        <div className="grid gap-6">
          {filteredGames.map((game) => (
            <Card 
              key={game.id}
              className={selectedGame?.id === game.id ? 'border-primary' : ''}
            >
              <CardHeader className="cursor-pointer" onClick={() => setSelectedGame(game)}>
                <CardTitle className="flex justify-between items-center">
                  <div>
                    <span>{game.title || 'Untitled Game'}</span>
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      ({game.type === 'best_of_three' ? 'Best of 3' : 'Total Score'})
                    </span>
                  </div>
                  <div className="text-sm font-normal text-muted-foreground">
                    {game.mode} • {game.current_players} players
                  </div>
                </CardTitle>
              </CardHeader>
              {selectedGame?.id === game.id && (
                <CardContent>
                  <div className="space-y-4">
                    {scores.map((score, index) => (
                      <div
                        key={score.id}
                        className={`flex items-center justify-between p-4 rounded-lg transition-all
                          ${index < 3 ? 'bg-primary/5' : 'bg-muted/50'}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center justify-center w-8">
                            {getMedalIcon(index)}
                          </div>
                          <div>
                            <p className="font-medium">
                              {score.profiles?.username || 'Anonymous Player'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Attempt {score.attempt_number}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold">{score.score.toLocaleString()}</p>
                          <p className="text-sm text-muted-foreground">
                            {score.response_time.toFixed(2)}s
                          </p>
                        </div>
                      </div>
                    ))}

                    {scores.length === 0 && (
                      <div className="text-center py-12">
                        <p className="text-muted-foreground">
                          No scores recorded yet
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}

          {filteredGames.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No games found matching your search criteria
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}